import { ZfLoadingSpinner, ZfStack, ZfTypography } from '@zafire/react';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessions } from '../api';
import type { ClientSessionDto } from '../api/generated/client';
import { SessionTable } from '../components/session/SessionTable';
import { SessionTableFilter } from '../components/session/SessionTableFilter';
import type { ISessionFilter } from '../interfaces/client';

const clientFullNameFilterFn = (session: ClientSessionDto, filter: ISessionFilter) =>
    filter.clientFullName
        ? `${session?.title} ${session.firstName} ${session.lastName}`?.toLowerCase().includes(filter.clientFullName.trim().toLowerCase())
        : true;
const rmFullNameFilterFn = (session: ClientSessionDto, filter: ISessionFilter) =>
    filter.rmFullName ? session.rmFullName?.toLowerCase().includes(filter.rmFullName.trim().toLowerCase()) : true;
const countryCodeFilterFn = (session: ClientSessionDto, filter: ISessionFilter) =>
    filter.countryCode ? session.mainDomicile?.countryCode?.toLowerCase().includes(filter.countryCode.trim().toLowerCase()) : true;
const lastModifiedDateFromFilterFn = (session: ClientSessionDto, filter: ISessionFilter) =>
    filter.updatedAtFromDate ? moment(session.updatedAt).isAfter(filter.updatedAtFromDate) : true;
const lastModifiedDateToFilterFn = (session: ClientSessionDto, filter: ISessionFilter) =>
    filter.updatedAtToDate ? moment(session.updatedAt).isBefore(filter.updatedAtToDate) : true;

export const SessionListScreen: FunctionComponent = () => {
    const { t } = useTranslation();
    const { data: sessions, isLoading, isSuccess } = useSessions();

    const [sessionFilters, setSessionFilters] = useState<ISessionFilter>({});

    const filteredSessions = useMemo(
        () =>
            sessionFilters
                ? sessions?.filter(
                      (session) =>
                          clientFullNameFilterFn(session, sessionFilters) &&
                          rmFullNameFilterFn(session, sessionFilters) &&
                          countryCodeFilterFn(session, sessionFilters) &&
                          lastModifiedDateFromFilterFn(session, sessionFilters) &&
                          lastModifiedDateToFilterFn(session, sessionFilters),
                  )
                : sessions,
        [sessions, sessionFilters],
    );

    return (
        <ZfStack direction="column" spacing="extra-large" className="session-screen">
            <ZfTypography variant="heading-medium" style={{ flexShrink: 0 }}>
                {t('navSessions')}
            </ZfTypography>
            <SessionTableFilter
                filters={sessionFilters}
                onFilterChange={setSessionFilters}
                availableCountryCodes={sessions?.map((session) => session.mainDomicile?.countryCode).filter((code) => code !== undefined) ?? []}
            />
            {isLoading && <ZfLoadingSpinner style={{ position: 'absolute', top: '50%', left: '50%' }} />}
            {isSuccess && (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <SessionTable sessions={filteredSessions ?? []} />
                </div>
            )}
        </ZfStack>
    );
};
